import React, { useEffect, useState } from 'react'
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  SimpleGrid,
  Button,
  Icon,
  Badge,
  Spinner
} from '@chakra-ui/react'
import { FiTrendingUp, FiEdit2, FiSettings, FiActivity, FiUsers, FiDollarSign, FiCalendar, FiMousePointer } from 'react-icons/fi'
import { FaFacebook, FaGoogle } from 'react-icons/fa'
import { SpotlightHeader } from '../components/Spotlight/SpotlightHeader'
import { SpotlightFooter } from '../components/Spotlight/SpotlightFooter'
import { VisibilityScore } from '../components/onboarding/VisibilityScore'
import { apiConfig } from '../config/api'

interface DashboardData {
  visibility_score: {
    current: number
    breakdown: { completeness: number; quality: number }
    recommendations: Array<{ action: string; potential_increase: number; category: string }>
  }
  ad_performance: {
    estimated_reach: number
    estimated_clicks: number
    estimated_conversions: number
    total_impressions_delivered: number
    total_clicks: number
  }
  recommendations: Array<{ action: string; potential_increase: number; category: string }>
  campaigns: Array<{
    id: string
    status: string
    impressions_total: number
    impressions_delivered: number
    pack_id: string
    selected_platforms?: string[]
    meta_status?: string
    google_status?: string
  }>
  onboarding_status: {
    step: number
    completed: boolean
    has_basic_info: boolean
    has_detailed_interview: boolean
  }
}

export const DashboardPage: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadDashboard = async () => {
      const token = localStorage.getItem('firebase_id_token')
      if (!token) {
        setError('Authentication required')
        setIsLoading(false)
        return
      }

      try {
        const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/onboarding/dashboard`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error('Failed to load dashboard')
        }

        const data = await response.json()
        setDashboardData(data)
      } catch (err: any) {
        setError(err.message || 'Failed to load dashboard')
      } finally {
        setIsLoading(false)
      }
    }

    loadDashboard()
  }, [])

  if (isLoading) {
    return (
      <Box minH="100vh" display="flex" flexDirection="column" bg="white">
        <SpotlightHeader />
        <Box flex={1} display="flex" alignItems="center" justifyContent="center">
          <VStack gap={4}>
            <Spinner size="xl" color="brand.500" />
            <Text>Loading dashboard...</Text>
          </VStack>
        </Box>
        <SpotlightFooter />
      </Box>
    )
  }

  if (error || !dashboardData) {
    return (
      <Box minH="100vh" display="flex" flexDirection="column" bg="white">
        <SpotlightHeader />
        <Box flex={1} display="flex" alignItems="center" justifyContent="center">
          <Text color="red.500">{error || 'Failed to load dashboard'}</Text>
        </Box>
        <SpotlightFooter />
      </Box>
    )
  }

  return (
    <Box minH="100vh" display="flex" flexDirection="column" bg="white">
      <SpotlightHeader />
      
      <Box flex={1} py={8}>
        <Container maxW="7xl">
          <VStack gap={8} align="stretch">
            {/* Header */}
            <HStack justify="space-between" align="end" flexWrap="wrap" gap={4}>
              <VStack gap={2} align="start">
                <Heading size={{ base: '2xl', md: '3xl' }} color="gray.900">
                  Dashboard
                </Heading>
                <Text fontSize="lg" color="gray.600">
                  Track your ad performance and visibility
                </Text>
              </VStack>
              <Button
                variant="outline"
                leftIcon={<Icon as={FiSettings} />}
                onClick={() => window.location.href = '/pricing'}
              >
                Billing & Settings
              </Button>
            </HStack>

            {/* Campaign Status (if any) */}
            {dashboardData.campaigns.length > 0 ? (
              <Card.Root borderColor="brand.200" borderWidth="1px" bg="brand.50">
                <Card.Body p={6}>
                  <VStack gap={4} align="stretch">
                    <HStack justify="space-between" align="center" flexWrap="wrap" gap={4}>
                      <HStack gap={3}>
                        <Box p={2} bg="white" borderRadius="full" shadow="sm">
                          <Icon as={FiActivity} color="brand.500" boxSize={6} />
                        </Box>
                        <VStack align="start" gap={0}>
                          <Text fontWeight="bold" fontSize="lg" color="gray.900">Active Campaign</Text>
                          <Text fontSize="sm" color="gray.600">
                            Status: {dashboardData.campaigns[0].status.toUpperCase()}
                          </Text>
                        </VStack>
                      </HStack>
                      <HStack gap={4}>
                        <VStack align="end" gap={0}>
                          <Text fontSize="xs" color="gray.500">Impressions Delivered</Text>
                          <Text fontWeight="bold" fontSize="md">
                            {dashboardData.campaigns[0].impressions_delivered.toLocaleString()} / {dashboardData.campaigns[0].impressions_total.toLocaleString()}
                          </Text>
                        </VStack>
                        <Button size="sm" colorPalette="brand" variant="outline">
                          View Details
                        </Button>
                      </HStack>
                    </HStack>
                    
                    {/* Platform Status Badges */}
                    <HStack gap={3} flexWrap="wrap">
                      {dashboardData.campaigns[0].selected_platforms?.includes('meta') && (
                        <HStack gap={2} bg="white" px={3} py={1} borderRadius="full" borderWidth="1px" borderColor="gray.200">
                          <Icon as={FaFacebook} color="blue.600" boxSize={4} />
                          <Text fontSize="sm" fontWeight="medium">Meta:</Text>
                          <Badge 
                            colorPalette={dashboardData.campaigns[0].meta_status === 'failed' ? 'red' : 'green'} 
                            variant="subtle"
                          >
                            {dashboardData.campaigns[0].meta_status?.toUpperCase() || 'ACTIVE'}
                          </Badge>
                        </HStack>
                      )}
                      {dashboardData.campaigns[0].selected_platforms?.includes('google') && (
                        <HStack gap={2} bg="white" px={3} py={1} borderRadius="full" borderWidth="1px" borderColor="gray.200">
                          <Icon as={FaGoogle} color="red.500" boxSize={4} />
                          <Text fontSize="sm" fontWeight="medium">Google:</Text>
                          <Badge 
                            colorPalette={dashboardData.campaigns[0].google_status === 'failed' ? 'red' : 'green'} 
                            variant="subtle"
                          >
                            {dashboardData.campaigns[0].google_status?.toUpperCase() || 'ACTIVE'}
                          </Badge>
                        </HStack>
                      )}
                      {/* Fallback for campaigns without selected_platforms (legacy) */}
                      {!dashboardData.campaigns[0].selected_platforms && dashboardData.campaigns[0].meta_status && (
                        <HStack gap={2} bg="white" px={3} py={1} borderRadius="full" borderWidth="1px" borderColor="gray.200">
                          <Icon as={FaFacebook} color="blue.600" boxSize={4} />
                          <Text fontSize="sm" fontWeight="medium">Meta:</Text>
                          <Badge 
                            colorPalette={dashboardData.campaigns[0].meta_status === 'failed' ? 'red' : 'green'} 
                            variant="subtle"
                          >
                            {dashboardData.campaigns[0].meta_status.toUpperCase()}
                          </Badge>
                        </HStack>
                      )}
                    </HStack>
                  </VStack>
                </Card.Body>
              </Card.Root>
            ) : (
              <Card.Root bg="gray.50" borderStyle="dashed">
                <Card.Body p={8} textAlign="center">
                  <VStack gap={3}>
                    <Icon as={FiActivity} color="gray.400" boxSize={8} />
                    <Text fontWeight="medium" color="gray.600">No active campaigns</Text>
                    <Button 
                      size="sm" 
                      colorPalette="brand" 
                      onClick={() => window.location.href = '/pricing'}
                    >
                      Start a Campaign
                    </Button>
                  </VStack>
                </Card.Body>
              </Card.Root>
            )}

            {/* Key Metrics Grid */}
            <Heading size="md" color="gray.900" mt={4}>
              Performance Overview
            </Heading>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={4}>
              <Card.Root>
                <Card.Body p={5}>
                  <VStack align="start" gap={2}>
                    <HStack justify="space-between" w="full">
                      <Text fontSize="sm" color="gray.500" fontWeight="medium">Leads</Text>
                      <Icon as={FiUsers} color="green.500" />
                    </HStack>
                    <Heading size="xl" color="gray.900">
                      {dashboardData.ad_performance.estimated_conversions}
                    </Heading>
                    <Text fontSize="xs" color="gray.400">Form submissions</Text>
                  </VStack>
                </Card.Body>
              </Card.Root>

              <Card.Root>
                <Card.Body p={5}>
                  <VStack align="start" gap={2}>
                    <HStack justify="space-between" w="full">
                      <Text fontSize="sm" color="gray.500" fontWeight="medium">Bookings</Text>
                      <Icon as={FiCalendar} color="purple.500" />
                    </HStack>
                    <Heading size="xl" color="gray.900">
                      0
                    </Heading>
                    <Text fontSize="xs" color="gray.400">Calendar scheduled</Text>
                  </VStack>
                </Card.Body>
              </Card.Root>

              <Card.Root>
                <Card.Body p={5}>
                  <VStack align="start" gap={2}>
                    <HStack justify="space-between" w="full">
                      <Text fontSize="sm" color="gray.500" fontWeight="medium">Impressions</Text>
                      <Icon as={FiActivity} color="blue.500" />
                    </HStack>
                    <Heading size="xl" color="gray.900">
                      {dashboardData.ad_performance.total_impressions_delivered.toLocaleString()}
                    </Heading>
                    <Text fontSize="xs" color="gray.400">Ad views</Text>
                  </VStack>
                </Card.Body>
              </Card.Root>

              <Card.Root>
                <Card.Body p={5}>
                  <VStack align="start" gap={2}>
                    <HStack justify="space-between" w="full">
                      <Text fontSize="sm" color="gray.500" fontWeight="medium">CTR</Text>
                      <Icon as={FiMousePointer} color="orange.500" />
                    </HStack>
                    <Heading size="xl" color="gray.900">
                      {dashboardData.ad_performance.total_impressions_delivered > 0 
                        ? ((dashboardData.ad_performance.total_clicks / dashboardData.ad_performance.total_impressions_delivered) * 100).toFixed(2)
                        : '0.00'}%
                    </Heading>
                    <Text fontSize="xs" color="gray.400">Click-through rate</Text>
                  </VStack>
                </Card.Body>
              </Card.Root>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} gap={6} mt={4}>
              {/* Visibility Score Widget */}
              <Card.Root>
                <Card.Body p={8}>
                  <VStack gap={4} align="stretch">
                    <HStack justify="space-between">
                      <Heading size="md" color="gray.900">
                        Profile Health
                      </Heading>
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => window.location.href = '/for-services/onboarding/details'}
                        leftIcon={<Icon as={FiEdit2} />}
                      >
                        Improve
                      </Button>
                    </HStack>
                    <VisibilityScore
                      score={dashboardData.visibility_score.current}
                      breakdown={dashboardData.visibility_score.breakdown}
                      recommendations={dashboardData.visibility_score.recommendations}
                      showBreakdown={true}
                      showRecommendations={true}
                    />
                  </VStack>
                </Card.Body>
              </Card.Root>

              {/* Spend / Budget (Placeholder) */}
              <Card.Root>
                <Card.Body p={8}>
                  <VStack gap={4} align="stretch">
                    <Heading size="md" color="gray.900">
                      Spend & Budget
                    </Heading>
                    <VStack gap={4} py={4}>
                      <HStack justify="space-between" w="full">
                        <Text color="gray.600">Total Spent</Text>
                        <Text fontWeight="bold">$0.00</Text>
                      </HStack>
                      <HStack justify="space-between" w="full">
                        <Text color="gray.600">Remaining Budget</Text>
                        <Text fontWeight="bold">
                          {dashboardData.campaigns.length > 0 ? 'Active Pack' : '$0.00'}
                        </Text>
                      </HStack>
                      <Box h="1px" bg="gray.100" w="full" />
                      <Button variant="outline" size="sm" w="full" leftIcon={<Icon as={FiDollarSign} />}>
                        View Invoices
                      </Button>
                    </VStack>
                  </VStack>
                </Card.Body>
              </Card.Root>
            </SimpleGrid>

          </VStack>
        </Container>
      </Box>

      <SpotlightFooter />
    </Box>
  )
}
